/**
 * Shared buy engine for the three curling stone magnet landing pages
 * (tf-curling-house, tf-curling-bonspiel, tf-curling-sheet).
 *
 * All three sell the same offer through very different markup, so the maths
 * lives here once and each section supplies its own elements by data attribute:
 *
 *   [data-tf-style="<variant id>"]  style/variant chooser
 *   [data-tf-tier="<n sets>"]       quantity tier chooser
 *   [data-tf-tier-price="<n sets>"] per-tier price readout
 *   [data-tf-total]                 current total
 *   [data-tf-was]                   pre-discount total (hidden when no saving)
 *   [data-tf-save]                  amount saved
 *   [data-tf-sets] / [data-tf-stones]
 *   [data-tf-meter-fill]            free-shipping progress bar
 *   [data-tf-meter-text]            free-shipping copy
 *   [data-tf-ship-note]             shipping status line
 *   [data-tf-variant-input]         hidden form input, variant id
 *   [data-tf-qty-input]             hidden form input, quantity
 *
 * The discount itself is a real Shopify automatic discount (percentage off at a
 * minimum quantity). The percentage and minimum are passed in from the section
 * settings so the page and the cart cannot drift apart — if the discount is
 * changed in admin, update the section settings to match.
 */

const TF_SELECTED = 'is-tf-selected';

class TfCurlingOffer extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-tf-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      return;
    }
    if (!this.variants.length) return;

    this.pct = parseFloat(this.dataset.discountPct || '0');
    this.minQty = parseInt(this.dataset.discountMinQty || '0', 10);
    this.threshold = parseInt(this.dataset.freeShipThreshold || '0', 10);
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.stonesPerSet = parseInt(this.dataset.stonesPerSet || '6', 10);

    this.styleButtons = Array.from(this.querySelectorAll('[data-tf-style]'));
    this.tierButtons = Array.from(this.querySelectorAll('[data-tf-tier]'));

    const preselected = this.styleButtons.find((b) => b.classList.contains(TF_SELECTED));
    this.variantId = preselected
      ? parseInt(preselected.dataset.tfStyle, 10)
      : this.variants[0].id;

    const preselectedTier = this.tierButtons.find((b) => b.classList.contains(TF_SELECTED));
    this.sets = preselectedTier ? parseInt(preselectedTier.dataset.tfTier, 10) : 1;

    this.styleButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.variantId = parseInt(btn.dataset.tfStyle, 10);
        this.render();
      });
    });

    this.tierButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.sets = parseInt(btn.dataset.tfTier, 10);
        this.render();
      });
    });

    this.render();
  }

  get variant() {
    return this.variants.find((v) => v.id === this.variantId) || this.variants[0];
  }

  /** Total in cents for `sets` of `variant`, after the automatic discount. */
  totalFor(variant, sets) {
    const gross = variant.price * sets;
    if (this.minQty > 0 && sets >= this.minQty && this.pct > 0) {
      return Math.round(gross * (1 - this.pct / 100));
    }
    return gross;
  }

  formatMoney(cents) {
    const format = this.moneyFormat;
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.round(cents / 100).toLocaleString('en-US');

    if (format.includes('amount_no_decimals')) {
      return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals);
    }
    if (format.includes('amount_with_comma_separator')) {
      return format.replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/,
        amount.replace('.', ',')
      );
    }
    return format.replace(/\{\{\s*amount\s*\}\}/, withCommas);
  }

  render() {
    const variant = this.variant;
    const total = this.totalFor(variant, this.sets);
    const gross = variant.price * this.sets;
    const saved = gross - total;
    const shipsFree = this.threshold > 0 && total >= this.threshold;
    const remaining = Math.max(0, this.threshold - total);

    this.styleButtons.forEach((btn) => {
      const on = parseInt(btn.dataset.tfStyle, 10) === this.variantId;
      btn.classList.toggle(TF_SELECTED, on);
      if (btn.hasAttribute('role')) btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    this.tierButtons.forEach((btn) => {
      const on = parseInt(btn.dataset.tfTier, 10) === this.sets;
      btn.classList.toggle(TF_SELECTED, on);
      if (btn.hasAttribute('role')) btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    // Per-tier prices follow the chosen style.
    this.querySelectorAll('[data-tf-tier-price]').forEach((el) => {
      const sets = parseInt(el.dataset.tfTierPrice, 10);
      el.textContent = this.formatMoney(this.totalFor(variant, sets));
    });

    this.#setText('[data-tf-total]', this.formatMoney(total));
    this.#setText('[data-tf-sets]', String(this.sets));
    this.#setText('[data-tf-stones]', String(this.sets * this.stonesPerSet));
    this.#setText('[data-tf-save]', saved > 0 ? this.formatMoney(saved) : '—');

    this.querySelectorAll('[data-tf-was]').forEach((el) => {
      el.textContent = this.formatMoney(gross);
      el.hidden = saved <= 0;
    });

    this.querySelectorAll('[data-tf-meter-fill]').forEach((el) => {
      const pct = this.threshold > 0 ? Math.min(100, (total / this.threshold) * 100) : 100;
      el.style.width = `${pct}%`;
    });

    this.#setText(
      '[data-tf-meter-text]',
      shipsFree
        ? this.dataset.copyShipsFree || 'Shipping is on us. This order ships free.'
        : // `[amount]`, not `{{amount}}` — Shopify parses `{{ }}` inside a JSON
          // template's setting values as a dynamic source and rejects the file.
          (this.dataset.copyShipsAway || '[amount] to go for free shipping.').replace(
            '[amount]',
            this.formatMoney(remaining)
          )
    );

    this.#setText(
      '[data-tf-ship-note]',
      shipsFree
        ? this.dataset.noteFree || 'Free shipping'
        : this.dataset.notePaid || 'Shipping calculated at checkout'
    );

    this.querySelectorAll('[data-tf-sticky-note]').forEach((el) => {
      el.textContent = `${this.sets} ${this.sets === 1 ? 'set' : 'sets'} · ${
        shipsFree ? 'ships free' : '+ shipping'
      }`;
    });

    // Keep the real product form in step with the visual state.
    this.querySelectorAll('[data-tf-variant-input]').forEach((el) => {
      el.value = String(this.variantId);
    });
    this.querySelectorAll('[data-tf-qty-input]').forEach((el) => {
      el.value = String(this.sets);
    });

    const addButton = this.querySelector('button[name="add"]');
    if (addButton) addButton.disabled = variant.available === false;

    this.querySelectorAll('[data-tf-soldout]').forEach((el) => {
      el.hidden = variant.available !== false;
    });

    // Swap any image bound to the chosen style.
    this.querySelectorAll('[data-tf-style-image]').forEach((el) => {
      if (variant.image) {
        el.src = variant.image;
        el.alt = variant.image_alt || variant.title;
      }
    });

    this.dispatchEvent(
      new CustomEvent('tf-offer:change', {
        bubbles: true,
        detail: { variantId: this.variantId, sets: this.sets, total, shipsFree },
      })
    );
  }

  #setText(selector, value) {
    this.querySelectorAll(selector).forEach((el) => {
      el.textContent = value;
    });
  }
}

if (!customElements.get('tf-curling-offer')) {
  customElements.define('tf-curling-offer', TfCurlingOffer);
}
